import React, { useState, useMemo } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

const ORANGE = '#ff922b';
const RED = '#ef4444';
const SLATE = '#94a3b8';
const GREEN = '#16a34a';
const AMBER = '#d97706';

const PAGE_SIZE = 12;

function effColor(pct) {
  if (pct >= 60) return GREEN;
  if (pct >= 40) return AMBER;
  return RED;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const over = row.actual > row.estimated;
  return (
    <div className="min-w-[190px] rounded-xl border border-gray-100 bg-white px-4 py-3 text-xs shadow-xl">
      <p className="mb-2 font-semibold text-gray-900">{label}</p>
      <div className="flex justify-between gap-4 py-0.5">
        <span className="text-gray-400">Estimated</span>
        <span className="font-semibold text-gray-700">{row.estimated}h</span>
      </div>
      <div className="flex justify-between gap-4 py-0.5">
        <span className="text-gray-400">Actual</span>
        <span className={`font-semibold ${over ? 'text-red-500' : 'text-green-600'}`}>{row.actual}h</span>
      </div>
      <div className="flex justify-between gap-4 py-0.5">
        <span className="text-gray-400">Variance</span>
        <span className={`font-semibold ${row.variance > 0 ? 'text-red-500' : 'text-green-600'}`}>
          {row.variance > 0 ? '+' : ''}{row.variance}h
        </span>
      </div>
      {row.efficiency > 0 && (
        <div className="flex justify-between gap-4 py-0.5">
          <span className="text-gray-400">Efficiency</span>
          <span className="font-semibold" style={{ color: effColor(row.efficiency) }}>{row.efficiency}%</span>
        </div>
      )}
      {row.rework > 0 && (
        <div className="flex justify-between gap-4 py-0.5">
          <span className="text-gray-400">Rework</span>
          <span className="font-semibold text-red-500">{row.rework}</span>
        </div>
      )}
    </div>
  );
}

export default function BarChartStacked({ dbData, title, subTitle }) {
  const [sortBy, setSortBy] = useState('variance');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const allRows = useMemo(() => {
    let rows = (dbData || []).map((r) => ({
      name: r.member,
      estimated: Number(r.inprogressEstimatedTime) || 0,
      actual: Number(r.actualInprogressTime) || 0,
      variance: Math.round(((Number(r.actualInprogressTime) || 0) - (Number(r.inprogressEstimatedTime) || 0)) * 10) / 10,
      efficiency: Math.round((Number(r.inprogressEfficiencyPercentage) || 0) * 10) / 10,
      rework: Number(r.inprogressReworkCount) || 0,
    }));

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }

    if (sortBy === 'variance') rows.sort((a, b) => b.variance - a.variance);
    if (sortBy === 'efficiency') rows.sort((a, b) => a.efficiency - b.efficiency);
    if (sortBy === 'name') rows.sort((a, b) => a.name.localeCompare(b.name));

    return rows;
  }, [dbData, sortBy, search]);

  const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = allRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const flagged = allRows.filter((d) => d.variance > 20).length;
  const chartHeight = Math.max(300, pageRows.length * 32 + 40);

  function handleSort(key) {
    setSortBy(key);
    setPage(0);
  }

  function handleSearch(val) {
    setSearch(val);
    setPage(0);
  }

  return (
    <div className="mt-0 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-300 hover:shadow-lg">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-[#0B6BCB]">{title}</h3>
          <p className="text-xs text-gray-400">
            {subTitle} &middot; {allRows.length} member{allRows.length !== 1 ? 's' : ''}
          </p>
        </div>

        {flagged > 0 && (
          <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
            {flagged} over estimate (+20h)
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: 'variance', label: 'Variance' },
            { key: 'efficiency', label: 'Efficiency' },
            { key: 'name', label: 'Name' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleSort(opt.key)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                sortBy === opt.key
                  ? 'border-orange-300 bg-orange-50 text-orange-600'
                  : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <input
          placeholder="Search name..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-44 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-[#262673] focus:bg-white"
        />
      </div>

      {/* Legend */}
      <div className="mb-2 flex flex-wrap gap-4 text-[11px] font-medium text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: SLATE }} />
          Estimated hrs
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: ORANGE }} />
          Actual (on/under)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: RED }} />
          Actual (over estimate)
        </span>
      </div>

      {/* Chart — fixed page size, so height never runs away */}
      {pageRows.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-sm text-gray-400">
          No members match this filter.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart data={pageRows} layout="vertical" margin={{ top: 4, right: 30, left: 4, bottom: 4 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
              label={{ value: 'Hours', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tick={{ fill: '#374151', fontSize: 11.5, fontWeight: 500 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Bar dataKey="estimated" fill={SLATE} radius={[4, 4, 4, 4]} barSize={9} opacity={0.5} />
            <Bar dataKey="actual" radius={[4, 4, 4, 4]} barSize={9}>
              {pageRows.map((row, i) => (
                <Cell key={i} fill={row.variance > 0 ? RED : ORANGE} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {/* Pagination */}
      {allRows.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-400">
            Showing {safePage * PAGE_SIZE + 1}
            &ndash;{Math.min((safePage + 1) * PAGE_SIZE, allRows.length)} of {allRows.length}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <IoChevronBack size={14} />
            </button>
            <span className="px-2 text-xs font-medium text-gray-600">
              {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <IoChevronForward size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}